import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FileText } from 'lucide-react';
import './DepositReceiptAnnouncement.css';

const ENDPOINT = '/api/announcements/deposit-receipts';

function DepositReceiptAnnouncement({ user, token }) {
  const [visible, setVisible] = useState(false);
  const [expiresInMs, setExpiresInMs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const buttonRef = useRef(null);
  const identity = `${user?.id || ''}:${token || ''}`;
  const identityRef = useRef(identity);
  identityRef.current = identity;

  useEffect(() => {
    let cancelled = false;
    setVisible(false);
    setExpiresInMs(null);
    setSaving(false);
    setError('');
    if (user?.role !== 'salesperson' || !token) return () => { cancelled = true; };
    axios.get(ENDPOINT, { headers: { Authorization: `Bearer ${token}` } })
      .then(response => {
        if (cancelled) return;
        setVisible(response.data.show === true);
        setExpiresInMs(Number.isFinite(response.data.expires_in_ms) ? response.data.expires_in_ms : null);
      })
      .catch(() => { if (!cancelled) setVisible(false); });
    return () => { cancelled = true; };
  }, [user?.id, user?.role, token]);

  useEffect(() => {
    if (!visible || expiresInMs == null) return undefined;
    const timeout = window.setTimeout(() => setVisible(false), Math.max(0, expiresInMs));
    return () => window.clearTimeout(timeout);
  }, [visible, expiresInMs]);

  useEffect(() => {
    if (!visible) return undefined;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    buttonRef.current?.focus();
    const keepFocus = event => {
      if (event.key === 'Tab' || event.key === 'Escape') {
        event.preventDefault();
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', keepFocus);
    return () => {
      document.removeEventListener('keydown', keepFocus);
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [visible]);

  async function acknowledge() {
    if (saving) return;
    const requestIdentity = identity;
    setSaving(true);
    setError('');
    try {
      await axios.post(`${ENDPOINT}/acknowledge`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (identityRef.current === requestIdentity) setVisible(false);
    } catch (saveError) {
      if (identityRef.current !== requestIdentity) return;
      if (saveError.response?.status === 410) setVisible(false);
      else setError('Could not save. Please try again.');
    } finally {
      if (identityRef.current === requestIdentity) setSaving(false);
    }
  }

  if (!visible || user?.role !== 'salesperson' || !token) return null;

  return <div className="dra-overlay">
    <section className="dra-dialog" role="dialog" aria-modal="true" aria-labelledby="dra-title" aria-describedby="dra-description">
      <span className="dra-icon" aria-hidden="true"><FileText size={27} strokeWidth={1.8} /></span>
      <span className="dra-eyebrow">Booking update · JQ &amp; Zen</span>
      <h2 id="dra-title">Payment slip required</h2>
      <p id="dra-description" className="dra-description">Attach the client's bank transfer receipt or terminal slip for every deposit payment. This is compulsory for verification.</p>
      <p className="dra-note">If the deposit is split into two payments, attach two slips.</p>
      <div className="dra-footer">
        {error && <p className="dra-error" role="alert">{error}</p>}
        <button ref={buttonRef} type="button" onClick={acknowledge} disabled={saving}>{saving ? 'Saving…' : 'Got it'}</button>
      </div>
    </section>
  </div>;
}

export default DepositReceiptAnnouncement;
